/**
 * AI Query Classifier
 *
 * Strict read-only query classifier for AI execution gating.
 * This is a hard gate (not a soft confirmation) - conservative by design.
 *
 * Process-agnostic: no Electron/React imports.
 *
 * The multi-statement refusal is driven by `projectCodeOnly` (the sql-grammar
 * code projection), NOT by a lexer of its own: that walker already models every
 * dialect rule a hidden `;` can hide behind — MySQL escape/ANSI quote modes,
 * PostgreSQL dollar quotes and `E'…'`, Oracle `q'<x>…<x>'`, T-SQL/SQLite
 * brackets, MySQL backticks and `#` comments, MySQL executable `/*!…*\/`
 * payloads (which the server RUNS), PostgreSQL nested block comments. See
 * `classifyQueryForAi` for how a caller WITHOUT an engine combines them.
 */
import type { CodeProjection } from '../../features/sql-grammar/document/codeProjection';
import { safetyProjections } from '../../features/sql-grammar/document/safetyProjections';
import type { SqlEngine } from '../../features/sql-grammar/model';

/**
 * Result of classifying a query for AI execution.
 */
export type AiQueryClassificationResult = {
  allowed: boolean;
  reason?: string;
  classification: 'read' | 'write' | 'unknown';
};

/**
 * Allowed read-only statement head keywords (case-insensitive).
 * Conservative allowlist - deny anything not explicitly allowed.
 */
const READ_ONLY_KEYWORDS = new Set([
  'SELECT',
  'WITH', // CTEs - will be followed by SELECT
  'EXPLAIN',
  'SHOW',
  'PRAGMA',
  'DESCRIBE',
  'DESC',
  'SP_HELP', // MSSQL sp_help variants are read-only
  'SP_HELPTEXT',
  'SP_HELPINDEX',
  'SP_COLUMNS',
  'SP_TABLES',
]);

/**
 * Keywords that indicate write operations.
 * Used for classification, not gating (we use allowlist for gating).
 */
const WRITE_KEYWORDS = new Set([
  'INSERT',
  'UPDATE',
  'DELETE',
  'MERGE',
  'CREATE',
  'ALTER',
  'DROP',
  'TRUNCATE',
  'GRANT',
  'REVOKE',
  'EXEC',
  'EXECUTE',
  'CALL',
  'SET',
  'USE',
  'BEGIN',
  'COMMIT',
  'ROLLBACK',
  'SAVEPOINT',
  'DECLARE',
  'BULK',
  'BACKUP',
  'RESTORE',
  'DBCC',
  'KILL',
  'SHUTDOWN',
  'RECONFIGURE',
  'DENY',
  'OPEN',
  'CLOSE',
  'FETCH',
  'DEALLOCATE',
]);

/** Oracle identifier characters — a `q`/`nq` preceded by one belongs to that
 *  identifier, so `seq'x'` is not an alternative-quote opener. */
const ORACLE_IDENT = /[A-Za-z0-9_$#]/;
const Q_QUOTE_CLOSER: Record<string, string> = { '[': ']', '{': '}', '(': ')', '<': '>' };

/**
 * End of the Oracle alternative-quote literal (`q'<open>…<close>'`, optional
 * `n` prefix, case-insensitive) starting at `i`, `-1` when it never closes, or
 * `null` when `i` does not open one. Mirrors `oracleExecutionSplit.ts` (which
 * this module must not import — it is renderer-and-main shared, Electron-free).
 *
 * Release review 1.5.0 R3 P1b: without this, `SELECT q'[']' FROM dual; DELETE
 * FROM t; SELECT q'[']' FROM dual` read as ONE read-only statement here (the
 * stray `'` characters paired up across the separators), so the multi-statement
 * refusal never fired and the Oracle adapter's own splitter ran the DELETE.
 */
const qQuoteEnd = (query: string, i: number): number | null => {
  if (i > 0 && ORACLE_IDENT.test(query[i - 1])) return null;
  const q = query[i] === 'n' || query[i] === 'N' ? i + 1 : i;
  if (query[q] !== 'q' && query[q] !== 'Q') return null;
  if (query[q + 1] !== "'") return null;
  const open = query[q + 2];
  if (open === undefined || /\s/.test(open)) return null;
  const at = query.indexOf(`${Q_QUOTE_CLOSER[open] ?? open}'`, q + 3);
  return at < 0 ? -1 : at + 2;
};

/**
 * The two faithful backslash readings of a string literal, strictest first.
 * `true` = `\'` escapes the quote (MySQL/MariaDB, and PostgreSQL `E'…'`);
 * `false` = `\` is an ordinary character and `'a\'` is a COMPLETE string
 * (PostgreSQL with the default `standard_conforming_strings = on`, Oracle,
 * SQL Server, SQLite). Release review 1.5.0 R5 P1: the head/`INTO` scanners
 * hard-coded the first reading, so a legitimate `SELECT 'a\' AS x` on any of
 * the four backslash-free engines was refused as "unmatched quotes". Callers
 * take the FIRST reading that parses for the head, and refuse if ANY reading
 * that parses shows a non-read construct.
 */
const STRING_LEX_READINGS: readonly boolean[] = [true, false];

/**
 * Strip string literals from a SQL statement for pattern matching.
 * Replaces string content with empty strings to avoid false matches.
 * Returns null if parsing is ambiguous (unmatched quotes).
 */
const stripStringLiterals = (statement: string, backslashEscapes: boolean): string | null => {
  let result = '';
  let inString: string | null = null;
  let escaped = false;

  for (let i = 0; i < statement.length; i++) {
    const char = statement[i];

    if (escaped) {
      if (!inString) result += char;
      escaped = false;
      continue;
    }

    if (backslashEscapes && char === '\\') {
      if (!inString) result += char;
      escaped = true;
      continue;
    }

    // Oracle q-quote: the whole literal is content, so its stray apostrophes
    // never re-open a string (R3 P1b — `q'[it's]'`, the canonical use of the
    // form, otherwise ended the scan mid-string and refused a plain read).
    if (!inString) {
      const qEnd = qQuoteEnd(statement, i);
      if (qEnd !== null) {
        if (qEnd < 0) return null;
        result += "''";
        i = qEnd - 1;
        continue;
      }
    }

    if (inString) {
      if (char === inString) {
        // Check for doubled quote (SQL escape)
        if (statement[i + 1] === inString) {
          i++; // Skip the doubled quote
        } else {
          inString = null;
          result += char; // Add closing quote
        }
      }
      continue;
    }

    if (char === "'" || char === '"') {
      inString = char;
      result += char; // Add opening quote
      continue;
    }

    result += char;
  }

  // If we ended inside a string, parsing is ambiguous
  if (inString) {
    return null;
  }

  return result;
};

/**
 * Strip SQL comments from a statement.
 * Handles both -- line comments and /* block comments *\/.
 * Returns null if parsing is ambiguous (unclosed block comment).
 */
const stripComments = (statement: string): string | null => {
  let result = '';
  let i = 0;

  while (i < statement.length) {
    // Check for line comment
    if (statement[i] === '-' && statement[i + 1] === '-') {
      // Skip until end of line
      while (i < statement.length && statement[i] !== '\n') {
        i++;
      }
      // Include the newline if present
      if (i < statement.length) {
        result += statement[i];
        i++;
      }
      continue;
    }

    // Check for block comment
    if (statement[i] === '/' && statement[i + 1] === '*') {
      i += 2; // Skip /*
      // Find the closing */
      let closed = false;
      while (i < statement.length - 1) {
        if (statement[i] === '*' && statement[i + 1] === '/') {
          i += 2; // Skip */
          closed = true;
          break;
        }
        i++;
      }
      if (!closed) {
        // Unclosed block comment
        return null;
      }
      result += ' '; // Replace comment with space to preserve word boundaries
      continue;
    }

    result += statement[i];
    i++;
  }

  return result;
};

/**
 * The dialects whose lexers a hidden second statement has to survive. The
 * classifier is engine-agnostic for its AI callers, so it takes the split
 * under EVERY dialect and the strictest answer wins (see `classifyQueryForAi`).
 */
const SPLIT_DIALECTS: readonly SqlEngine[] = ['mssql', 'postgres', 'mysql', 'oracle', 'sqlite'];

/**
 * Split a query into statements at the `;` characters that are CODE under one
 * dialect's lexer, or `null` when that lexer ends inside an unterminated
 * string / comment / quoted identifier / dollar quote (a syntax error on that
 * engine, so nothing runs).
 *
 * The split is read off `projectCodeOnly`, which masks literals and comments
 * while preserving offsets — one shared lexer with the dialect-aware denylist
 * in `sql-safety/readOnlySql`, instead of a second hand-rolled one here.
 * Release review 1.5.0 R5 P1: the hand-rolled splitter treated `\` as a string
 * escape for EVERY engine and had no dollar-quote model, so `SELECT 'a\' AS x;
 * DO $$ BEGIN DELETE FROM customers; END $$; SELECT 'b\' AS y` (PostgreSQL)
 * and `SELECT 'a\' FROM dual; COMMENT ON TABLE t IS 'pwned'; SELECT 'b\' FROM
 * dual` (Oracle) each read as ONE read-only statement — both live-verified as
 * previews that ran the hidden write.
 */
/**
 * T-SQL `GO` is a CLIENT-side batch separator: SQL Server never sees it, the
 * tools split on it — and so does `mssqlDriverAdapter.ts` before execution.
 * A batch hidden behind one is therefore a second statement exactly like one
 * behind a `;` (release review 1.5.0 R6: `SELECT 1\nGO\nCHECKPOINT` classified
 * as one read-only statement while the adapter ran both batches). Only a line
 * that is nothing but `GO [count]` counts, and it is read off the projection,
 * so the same letters inside a string or comment (masked to spaces) cannot
 * match. No other dialect has the separator, and a bare `GO` line is a syntax
 * error on them, so they never split here.
 */
const GO_BATCH_LINE = /^[ \t]*GO(?:[ \t]+\d+)?[ \t]*\r?$/i;
const goBatchSeparators = (code: string, engine: SqlEngine): ReadonlyArray<readonly [number, number]> => {
  if (engine !== 'mssql') return [];
  const ranges: Array<readonly [number, number]> = [];
  let lineStart = 0;
  for (let i = 0; i <= code.length; i++) {
    if (i < code.length && code[i] !== '\n') continue;
    if (GO_BATCH_LINE.test(code.slice(lineStart, i))) ranges.push([lineStart, i]);
    lineStart = i + 1;
  }
  return ranges;
};

const splitStatementsForDialect = (query: string, engine: SqlEngine, projection: CodeProjection): string[] | null => {
  const statements: string[] = [];
  const goRanges = goBatchSeparators(projection.code, engine);
  let nextGo = 0;
  let start = 0;
  for (let i = 0; i < projection.code.length; i++) {
    const go = goRanges[nextGo];
    if (go && i === go[0]) {
      const unit = query.slice(start, i).trim();
      if (unit) statements.push(unit);
      start = go[1];
      i = go[1] - 1;
      nextGo += 1;
      continue;
    }
    if (projection.code[i] !== ';') continue;
    const unit = query.slice(start, i).trim();
    if (unit) statements.push(unit);
    start = i + 1;
  }
  const tail = query.slice(start).trim();
  if (tail) statements.push(tail);
  // A later syntax error does not undo statements already run by MySQL.
  if (projection.ambiguous && statements.length < 2) return null;
  return statements;
};

/**
 * Extract the first keyword(s) from a SQL statement.
 * Handles WITH...SELECT (CTEs) by checking both keywords.
 */
const extractStatementHead = (statement: string): string | null => {
  // Strip comments first
  const noComments = stripComments(statement);
  if (noComments === null) {
    return null;
  }

  // Strip string literals under the first backslash reading that parses.
  const stripped = STRING_LEX_READINGS.map((backslashEscapes) =>
    stripStringLiterals(noComments, backslashEscapes),
  ).find((candidate): candidate is string => candidate !== null);
  if (stripped === undefined) {
    return null;
  }

  // Normalize whitespace and extract first token
  const normalized = stripped.trim().replace(/\s+/g, ' ');
  const tokens = normalized.split(' ').filter((t) => t.length > 0);

  if (tokens.length === 0) {
    return null;
  }

  const firstToken = tokens[0].toUpperCase();

  // Handle CTE: WITH ... SELECT/INSERT/UPDATE/DELETE
  if (firstToken === 'WITH') {
    // CTEs can be followed by SELECT (read-only) or INSERT/UPDATE/DELETE (writable)
    // We need to find the main statement keyword after the CTE definition(s)
    // CTEs have the form: WITH name AS (...), ... SELECT/INSERT/UPDATE/DELETE
    const upperNormalized = normalized.toUpperCase();

    // Check for write keywords first (they take precedence)
    // Look for keywords outside of parentheses at the "top level"
    const writeKeywordsToCheck = ['INSERT', 'UPDATE', 'DELETE', 'MERGE'];
    for (let i = 0; i < writeKeywordsToCheck.length; i++) {
      const writeKw = writeKeywordsToCheck[i];
      // Look for the keyword followed by space or as end of string
      const patterns = [` ${writeKw} `, `)${writeKw} `, `) ${writeKw} `, `)${writeKw}`];
      for (let j = 0; j < patterns.length; j++) {
        if (upperNormalized.includes(patterns[j])) {
          return writeKw; // It's a writable CTE
        }
      }
      // Check if statement ends with the write keyword
      if (upperNormalized.endsWith(` ${writeKw}`) || upperNormalized.endsWith(`)${writeKw}`)) {
        return writeKw;
      }
    }

    // If no write keyword found, check for SELECT (read-only CTE)
    if (upperNormalized.includes(' SELECT ') || upperNormalized.includes(')SELECT')) {
      return 'WITH'; // WITH...SELECT is read-only
    }

    return 'WITH'; // Default to WITH if no main statement keyword found
  }

  return firstToken;
};

/**
 * SQLite read-only pragmas that legitimately take a parenthesized argument:
 * the argument names an object to introspect or bounds a check — it never
 * sets connection/database state. Because settable pragmas share the
 * `PRAGMA x(value)` function-call syntax (`PRAGMA foreign_keys(OFF)`), we
 * can't blanket-deny parentheses; we allow these known reads to carry one
 * and treat any OTHER argument-carrying pragma as a write.
 */
const READ_PRAGMAS_WITH_ARG = new Set([
  'TABLE_INFO',
  'TABLE_XINFO',
  'INDEX_INFO',
  'INDEX_XINFO',
  'INDEX_LIST',
  'FOREIGN_KEY_LIST',
  'FOREIGN_KEY_CHECK',
  'TABLE_LIST',
  'INTEGRITY_CHECK',
  'QUICK_CHECK',
  'COLLATION_LIST',
  'FUNCTION_LIST',
  'MODULE_LIST',
  'PRAGMA_LIST',
  'DATABASE_LIST',
  'COMPILE_OPTIONS',
  'STATS',
]);

/**
 * SQLite pragmas that perform maintenance / mutate state even in the bare
 * `PRAGMA x` form (no argument) — a scheduler or AI agent must never run
 * these under a read-only policy.
 */
const ALWAYS_WRITE_PRAGMAS = new Set([
  'OPTIMIZE',
  'WAL_CHECKPOINT',
  'INCREMENTAL_VACUUM',
  'SHRINK_MEMORY',
  'VACUUM',
]);

/**
 * A pragma name (or schema qualifier) as a bare identifier OR a quoted /
 * bracketed identifier: `"foreign_keys"`, `'foreign_keys'`, `` `foreign_keys` ``,
 * `[foreign_keys]`. SQLite treats all of these as the same identifier, so the
 * gate must too (a quoted name is NOT a way to smuggle a setter past it).
 * Quote delimiters escape by doubling (`"a""b"` is the identifier `a"b`), so
 * the pattern consumes doubled delimiters — otherwise it would stop early and
 * a schema qualifier like `"ma""in".foreign_keys(OFF)` would hide the setter.
 */
const PRAGMA_IDENT = String.raw`(?:[^\s.(=;'"\x60\[\]]+|"(?:[^"]|"")*"|'(?:[^']|'')*'|\x60(?:[^\x60]|\x60\x60)*\x60|\[[^\]]*\])`;

/** Strip surrounding "", '', ``, or [] quoting (and un-double escapes) from an identifier. */
const dequotePragmaName = (raw: string): string => {
  if (raw.length >= 2) {
    const first = raw[0];
    const last = raw[raw.length - 1];
    if (first === '"' && last === '"') return raw.slice(1, -1).replace(/""/g, '"');
    if (first === "'" && last === "'") return raw.slice(1, -1).replace(/''/g, "'");
    if (first === '`' && last === '`') return raw.slice(1, -1).replace(/``/g, '`');
    if (first === '[' && last === ']') return raw.slice(1, -1);
  }
  return raw;
};

/**
 * Reclassify a SQLite `PRAGMA` statement as a write when it mutates state.
 * A bare `PRAGMA x` reads the current value, but a pragma that carries a
 * value — either `PRAGMA x = y` (assignment) OR the equivalent function-call
 * setter `PRAGMA x(y)` — writes (foreign_keys, journal_mode, writable_schema,
 * secure_delete, …). The setter form is the easy-to-miss vector because
 * read-only introspection pragmas use the SAME syntax (`PRAGMA table_info(t)`),
 * so we allowlist those reads and flag every other argument-carrying pragma.
 * The name may be quoted/bracketed (`PRAGMA "foreign_keys"(OFF)`) — we dequote
 * it before the allowlist check so a quoted setter is caught AND a quoted
 * introspection read (`PRAGMA "table_info"(t)`) stays a read. `body` is
 * comment-stripped, upper-cased, and single-spaced but NOT string-stripped
 * (string-stripping would empty a quoted name).
 */
const detectPragmaWrite = (body: string): string | null => {
  // Optional `schema.` qualifier (PRAGMA main.foreign_keys = OFF), then the
  // pragma name, then an optional `=` (assignment) or `(` (function setter).
  const trimmed = body.trim();
  const match = new RegExp(
    String.raw`^PRAGMA\s+(?:${PRAGMA_IDENT}\s*\.\s*)?(${PRAGMA_IDENT})\s*(=|\()?`,
  ).exec(trimmed);
  if (!match) {
    // Head we couldn't parse a name from at all — if it still carries a value
    // (`=` or `(`), deny conservatively; otherwise it's a bare read.
    return /[=(]/.test(trimmed) ? 'PRAGMA with a value modifies database state' : null;
  }
  const name = dequotePragmaName(match[1]);
  if (ALWAYS_WRITE_PRAGMAS.has(name)) {
    return `PRAGMA ${name} performs maintenance work and is not read-only`;
  }
  const hasArgument = match[2] !== undefined;
  if (hasArgument) {
    return READ_PRAGMAS_WITH_ARG.has(name)
      ? null
      : 'PRAGMA with a value argument modifies database state';
  }
  // No `=`/`(` was consumed, so this must be a clean bare read
  // (`PRAGMA foreign_keys`, optional trailing `;`). The ONLY acceptable leftover
  // after the name is nothing or a lone `;` — ANY other suffix means the name
  // parsed only partially (an exotic char, an escaped-quote schema, a second
  // statement) and a real setter may be hiding past it, so deny conservatively.
  const rest = trimmed.slice(match[0].length).trim();
  if (rest !== '' && rest !== ';') {
    return 'PRAGMA has unexpected trailing structure and may hide a setter; treated as a write';
  }
  return null;
};

/**
 * Read-allowed statement HEADS can still smuggle non-read effects through
 * dialect constructs (C3-R2 hardening): `SELECT … INTO` creates tables
 * (mssql/pgsql) or writes server files (mysql OUTFILE/DUMPFILE); locking
 * clauses (`FOR UPDATE`/`FOR SHARE`) take row locks a scheduler must never
 * hold; `PRAGMA x = y` / `PRAGMA x(y)` mutate database state (sqlite). Scan
 * the string/comment-stripped text and reclassify as write.
 * Deliberately conservative: mysql `SELECT … INTO @var` is also flagged.
 */
const detectNonReadConstruct = (statement: string, headKeyword: string): string | null => {
  const noComments = stripComments(statement);
  // Every backslash reading that parses gets a vote, and the strictest wins:
  // `\` is a string escape on MySQL and inside PostgreSQL `E'…'`, and an
  // ordinary character everywhere else, so neither reading alone may decide
  // what is or is not inside a literal here (R5 P1).
  const strippings =
    noComments === null
      ? []
      : STRING_LEX_READINGS.map((backslashEscapes) =>
          stripStringLiterals(noComments, backslashEscapes),
        ).filter((candidate): candidate is string => candidate !== null);
  if (strippings.length === 0) return 'Could not parse query structure';
  for (const stripped of strippings) {
    const normalized = ` ${stripped.trim().replace(/\s+/g, ' ').toUpperCase()} `;
    if ((headKeyword === 'SELECT' || headKeyword === 'WITH') && normalized.includes(' INTO ')) {
      return 'SELECT ... INTO writes outside the result set and is not read-only';
    }
    if (/ FOR (NO KEY )?(UPDATE|SHARE) | FOR KEY SHARE /.test(normalized)) {
      return 'Locking clauses (FOR UPDATE / FOR SHARE) are not read-only safe';
    }
  }
  if (headKeyword === 'PRAGMA') {
    // Parse the pragma from the comment-stripped but NOT string-stripped text,
    // so a quoted name (`PRAGMA "foreign_keys"(OFF)`) survives to be dequoted
    // and matched against the read allowlist rather than being emptied.
    const rawNormalized = (noComments ?? '').replace(/\s+/g, ' ').toUpperCase();
    return detectPragmaWrite(rawNormalized);
  }
  return null;
};

/**
 * Classify a SQL query for AI execution.
 *
 * Uses strict allowlist-based classification:
 * - Allow: SELECT, WITH...SELECT, EXPLAIN, SHOW, PRAGMA
 * - Deny: INSERT, UPDATE, DELETE, CREATE, ALTER, DROP, TRUNCATE, etc.
 * - Deny: SELECT INTO / locking clauses / PRAGMA assignment (read heads with
 *   non-read effects)
 * - Deny: Multi-statement queries
 * - Deny: Ambiguous parsing
 *
 * @param query The SQL query to classify
 * @param engine Grammar dialect of the connection, when the caller knows it
 *   (the dashboard policy and the auto-run guard do). Omit it for the AI /
 *   MCP callers, which classify before an engine is in hand.
 * @returns Classification result with allowed status and reason
 */
export const classifyQueryForAi = (
  query: string,
  engine?: SqlEngine,
): AiQueryClassificationResult => {
  // Empty query
  if (!query || !query.trim()) {
    return {
      allowed: false,
      reason: 'Empty query',
      classification: 'unknown',
    };
  }

  // Split into statements under the caller's dialect, or — with no engine —
  // under all five, because the query will run on exactly ONE of them and the
  // classifier cannot know which (R5 P1). MySQL also checks every distinct
  // quote-mode interpretation because the pooled session's mode is unknown.
  // Two rules, both required:
  //  • ANY dialect that finds a top-level `;` refuses the run. A separator one
  //    lexer hides (`'a\'` closes on PostgreSQL/Oracle/T-SQL/SQLite but escapes
  //    on MySQL; `$$…$$` is a literal on PostgreSQL and two `$` operators
  //    elsewhere; `[a'b]` is an identifier on T-SQL/SQLite and a string
  //    opener elsewhere) is still a separator on the engine that runs it.
  //  • A single unit ending mid-literal (`null`) only refuses when EVERY
  //    interpretation does. That keeps both `SELECT 'a\' AS x` (valid with
  //    NO_BACKSLASH_ESCAPES) and `SELECT 'it\'s' AS x` runnable on MySQL.
  //    Multiple units still refuse: earlier statements may execute before
  //    a later syntax error.
  const dialects = engine ? [engine] : SPLIT_DIALECTS;
  const splits = dialects.flatMap((dialect) =>
    safetyProjections(query, dialect).map(projection => splitStatementsForDialect(query, dialect, projection)),
  );

  if (splits.some((units) => units !== null && units.length > 1)) {
    return {
      allowed: false,
      reason: 'Multi-statement queries are not allowed for AI execution',
      classification: 'unknown',
    };
  }

  if (splits.every((units) => units === null)) {
    return {
      allowed: false,
      reason: 'Could not parse query structure - unmatched quotes',
      classification: 'unknown',
    };
  }

  const statement = splits.find(
    (units): units is string[] => units !== null && units.length === 1,
  )?.[0];

  // Every dialect that parsed found only separators and whitespace.
  if (statement === undefined) {
    return {
      allowed: false,
      reason: 'Empty query',
      classification: 'unknown',
    };
  }

  // Extract the statement head keyword
  const headKeyword = extractStatementHead(statement);

  if (headKeyword === null) {
    return {
      allowed: false,
      reason: 'Could not parse query structure',
      classification: 'unknown',
    };
  }

  // Check against allowlist (case-insensitive)
  if (READ_ONLY_KEYWORDS.has(headKeyword)) {
    const nonRead = detectNonReadConstruct(statement, headKeyword);
    if (nonRead) {
      return {
        allowed: false,
        reason: nonRead,
        classification: 'write',
      };
    }
    return {
      allowed: true,
      classification: 'read',
    };
  }

  // Check if it's a known write keyword for better classification
  if (WRITE_KEYWORDS.has(headKeyword)) {
    return {
      allowed: false,
      reason: `Write operation '${headKeyword}' is not allowed for AI execution`,
      classification: 'write',
    };
  }

  // Unknown keyword - deny by default
  return {
    allowed: false,
    reason: `Unknown statement type '${headKeyword}' is not allowed for AI execution`,
    classification: 'unknown',
  };
};
